import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch, useSelector } from 'react-redux';
import { apiClient } from '../../../utils/axios';
import { AxiosError } from 'axios';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';

export const fetchSalesInvoices = async (query) => {
  const { data } = await apiClient.get(`/sales-invoices`, { params: query.queryKey[1] });
  return data;
};

export const fetchPurchaseInvoices = async (query) => {
  const { data } = await apiClient.get(`/purchase-invoices`, { params: query.queryKey[1] });
  return data;
};

export const useGetInvoicesHandler = ({ searchQuery, isPurchase = false }) => {
  const dispatch = useDispatch();
  const { eInvoiceForm } = useTranslatedMessages();
  const messages = eInvoiceForm?.invoice_handlers || {};
  const [invoices, setInvoices] = useState([]);
  const [totalDocuments, setTotalDocuments] = useState(0);

  const {
    data: fetchedData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [isPurchase ? 'purchase-invoices' : 'sales-invoices', { ...searchQuery }], // A unique key for this query
    queryFn: isPurchase ? fetchPurchaseInvoices : fetchSalesInvoices,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: 'always',
    cacheTime: 0,
    retry: 3,
  });

  useEffect(() => {
    if (fetchedData) {
      setInvoices(fetchedData.content);
      setTotalDocuments(fetchedData.total_elements);
    }
  }, [fetchedData]);

  useEffect(() => {
    if (isError) {
      const fallbackMessage = messages.load_invoices_error ?? 'Failed to load invoices';
      if (error instanceof AxiosError) {
        if (error.response?.status) {
          dispatch(
            showSnackbar({
              message: error.response?.data?.message ?? fallbackMessage,
              severity: 'error',
            }),
          );
        } else {
          dispatch(showSnackbar({ message: error.message ?? fallbackMessage, severity: 'error' }));
        }
      } else {
        dispatch(
          showSnackbar({
            message: error?.message ?? fallbackMessage,
            severity: 'error',
          }),
        );
      }
    }
  }, [dispatch, error, isError, messages.load_invoices_error]);

  return {
    invoices,
    totalDocuments,
    isLoading,
  };
};
